/**
 * SyncModals — Task 25.5 (DEC-116). Edit-interval + trigger-confirm dialogs.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle, subText } from '../../ui/bits';
import { useUpdateSyncConfig, useTriggerSync, type SyncConfig } from './hooks';

const AGENCY_NAMES: Record<string, string> = {
  OCC: 'Oklahoma (OCC)',
  RRC: 'Texas (RRC)',
  WOGCC: 'Wyoming (WOGCC)',
  NMOCD: 'New Mexico (NMOCD)',
  NDIC: 'North Dakota (NDIC)',
};

export function agencyLabel(agency: string): string {
  return AGENCY_NAMES[agency] ?? agency;
}

export function EditIntervalModal({
  config,
  onOpenChange,
}: {
  config: SyncConfig | null;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const open = config !== null;
  const [interval, setIntervalDays] = React.useState('7');
  const [active, setActive] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const update = useUpdateSyncConfig(config?.stateAgency ?? '');

  React.useEffect(() => {
    if (config === null) return;
    setIntervalDays(String(config.syncIntervalDays));
    setActive(config.isActive);
    setError(null);
  }, [config]);

  const save = (): void => {
    const n = Number(interval);
    if (!Number.isInteger(n) || n < 1 || n > 365) {
      setError('Interval must be a whole number of days between 1 and 365.');
      return;
    }
    setError(null);
    update.mutate(
      { syncIntervalDays: n, isActive: active },
      {
        onSuccess: () => {
          toast.success('Sync config updated');
          onOpenChange(false);
        },
        onError: () => setError('Could not save changes.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{config !== null ? agencyLabel(config.stateAgency) : ''} sync</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="Sync interval (days, 1–365)" htmlFor="si-interval">
            <input
              id="si-interval"
              type="number"
              min={1}
              max={365}
              value={interval}
              onChange={(e): void => setIntervalDays(e.target.value)}
              style={{ ...fieldStyle, maxWidth: '140px' }}
            />
          </FormRow>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={active}
              onChange={(e): void => setActive(e.target.checked)}
            />
            <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>
              Active (uncheck to pause automatic syncing)
            </span>
          </label>
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={update.isPending}
            onClick={save}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TriggerConfirmModal({
  agency,
  onOpenChange,
}: {
  agency: string | null;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const open = agency !== null;
  const trigger = useTriggerSync(agency ?? '');

  const confirm = (): void => {
    trigger.mutate(
      {},
      {
        onSuccess: (r) => {
          if (r.triggered) toast.success('Sync started — check Run History for progress.');
          else toast.message(r.message);
          onOpenChange(false);
        },
        onError: () => toast.error('Could not trigger the sync.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trigger {agency !== null ? agencyLabel(agency) : ''} sync now?</DialogTitle>
        </DialogHeader>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          This will start a full catalog sync. Large syncs may take 15–20 minutes.
        </p>
        <p style={{ ...subText, textTransform: 'none' }}>
          Progress appears in Run History below once the task starts.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={trigger.isPending}
            onClick={confirm}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            {trigger.isPending ? 'Starting…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
